import {
	ADD_NEW_PAYMENT_METHOD,
	APPLY_SUBSCRIPTION_VOUCHER,
	CANCEL_NON_PAID_SUBSCRIPTION,
	CANCEL_SUBSCRIPTION,
	CREATE_ACADEMY_SUBSCRIBE_EMAIL,
	GET_MY_PAID_SUBSCRIPTIONS,
	GET_PAYMENT_CARDS,
	GET_SUBSCRIPTION_PAYMENT_CARDS,
	GET_SUBSCRIPTION_PAYMENT_SUMMARY,
	UPDATE_PAYMENT_CARD,
	UPDATE_SUBSCRIPTION_PLAN,
} from "@onair/operations/keys";
import { SubscriptionEmailType } from "@onair/repositories";
import {
	addNewPaymentMethod,
	applySubscriptionVoucher,
	cancelNonPaidSubscription,
	cancelSubscription,
	createAcademySubscribeEmail,
	createSubscription,
	subscriptionEmail,
	updatePaymentCard,
	updateSubscriptionPlan,
} from "@onair/services/subscription";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const mutationCreateSubscription = () => {
	return useMutation({
		mutationFn: ({
			membershipPriceId,
			voucher,
		}: {
			membershipPriceId: string;
			voucher?: string;
		}) => {
			return createSubscription(membershipPriceId, voucher);
		},
	});
};

export const useCancelSubscription = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: [CANCEL_SUBSCRIPTION],
		mutationFn: (subscriptionId: string) => cancelSubscription(subscriptionId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [GET_MY_PAID_SUBSCRIPTIONS] });
			queryClient.invalidateQueries({
				queryKey: [GET_SUBSCRIPTION_PAYMENT_SUMMARY],
			});
		},
	});
};

export const useCancelNonPaidSubscription = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: [CANCEL_NON_PAID_SUBSCRIPTION],
		mutationFn: (subscriptionId: string) =>
			cancelNonPaidSubscription(subscriptionId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [GET_MY_PAID_SUBSCRIPTIONS] });
		},
	});
};

export const useUpdatePaymentCard = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: [UPDATE_PAYMENT_CARD],
		mutationFn: ({
			subscriptionId,
			bankAccountId,
		}: {
			subscriptionId: string;
			bankAccountId: string;
		}) => updatePaymentCard(subscriptionId, bankAccountId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [GET_SUBSCRIPTION_PAYMENT_SUMMARY],
			});
			queryClient.invalidateQueries({ queryKey: [GET_PAYMENT_CARDS] });
			queryClient.invalidateQueries({
				queryKey: [GET_SUBSCRIPTION_PAYMENT_CARDS],
			});
		},
	});
};

export const useAddNewPaymentMethod = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: [ADD_NEW_PAYMENT_METHOD],
		mutationFn: () => addNewPaymentMethod(),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [GET_PAYMENT_CARDS] });
			queryClient.invalidateQueries({
				queryKey: [GET_SUBSCRIPTION_PAYMENT_CARDS],
			});
		},
	});
};

export const useUpdateSubscriptionPlan = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: [UPDATE_SUBSCRIPTION_PLAN],
		mutationFn: ({
			subscriptionId,
			membershipPriceId,
		}: {
			subscriptionId: string;
			membershipPriceId: string;
		}) => updateSubscriptionPlan(subscriptionId, membershipPriceId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [GET_MY_PAID_SUBSCRIPTIONS] });
			queryClient.invalidateQueries({
				queryKey: [GET_SUBSCRIPTION_PAYMENT_SUMMARY],
			});
		},
	});
};

export const useApplySubscriptionVoucher = () => {
	return useMutation({
		mutationKey: [APPLY_SUBSCRIPTION_VOUCHER],
		mutationFn: ({
			membershipPriceId,
			voucherCode,
		}: {
			membershipPriceId: string;
			voucherCode: string;
		}) => applySubscriptionVoucher(membershipPriceId, voucherCode),
	});
};

export const useCreateAcademySubscribeEmail = () => {
	return useMutation({
		mutationKey: [CREATE_ACADEMY_SUBSCRIBE_EMAIL],
		mutationFn: (email: string) => {
			return createAcademySubscribeEmail(email);
		},
	});
};

export const useSubscribeEmailMutation = () => {
	return useMutation({
		mutationKey: [CREATE_ACADEMY_SUBSCRIBE_EMAIL],
		mutationFn: subscriptionEmail,
	});
};

export { mutationCreateSubscription };
